import { useState, useEffect, useCallback } from 'react';
import { Point, Polygon } from '../helper/types';
import { distance, isPointInsidePolygon } from '../helper/floor-plan-gui.utils';
import { VERTEX_HIT_RADIUS } from '../helper/constants';

// ── useFloorPlanImage ─────────────────────────────────────────────────────────

/** Loads the floor-plan image; returns the HTMLImageElement once ready */
export const useFloorPlanImage = (url: string) => {
    const [image, setImage] = useState<HTMLImageElement | null>(null);

    useEffect(() => {
        if (!url) return;

        const img = new Image();
        // Do NOT set crossOrigin on presigned S3 URLs — the signature in the
        // query string already authenticates the request, and adding the
        // crossOrigin attribute causes the browser to send an Origin header
        // that S3 presigned URLs don't include in their CORS response.
        img.src = url;
        img.onload = () => setImage(img);
        img.onerror = () => setImage(null);
    }, [url]);

    return image;
};

// ── useCanvasSize ─────────────────────────────────────────────────────────────

/**
 * Watches a container element and returns dimensions that keep the canvas
 * aspect-ratio locked to the loaded image (or a 5:3 fallback).
 */
export const useCanvasSize = (containerRef: React.RefObject<HTMLDivElement>, image: HTMLImageElement | null) => {
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const updateSize = () => {
            if (!containerRef.current) return;
            const { width } = containerRef.current.getBoundingClientRect();
            const height = image ? (image.naturalHeight / image.naturalWidth) * width : width * 0.6;

            setCanvasSize((prev) => {
                const rounded = Math.round(height);
                if (prev.width === width && prev.height === rounded) return prev;
                return { width, height: rounded };
            });
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [containerRef, image]);

    return canvasSize;
};

// ── useHitTesting ─────────────────────────────────────────────────────────────

/**
 * Returns utility functions for finding which polygon / vertex / edge-midpoint
 * sits under a given canvas point.
 */
export const useHitTesting = (polygons: Polygon[]) => {
    /**
     * Finds the first vertex within VERTEX_HIT_RADIUS of `point`.
     * If `restrictToPolygonId` is provided, only that polygon is searched.
     */
    const findVertexAt = useCallback(
        (point: Point, restrictToPolygonId?: string) => {
            const candidates = restrictToPolygonId
                ? polygons.filter((p) => p.id === restrictToPolygonId)
                : [...polygons].reverse(); // topmost polygon first

            for (const polygon of candidates) {
                for (let i = 0; i < polygon.points.length; i++) {
                    if (distance(point, polygon.points[i]) <= VERTEX_HIT_RADIUS) {
                        return { polygonId: polygon.id, vertexIndex: i };
                    }
                }
            }
            return null;
        },
        [polygons]
    );

    /**
     * Checks whether `point` is near the midpoint of any edge of the given polygon.
     * Returns the edge's start-vertex index, or null.
     */
    const findEdgeMidpointAt = useCallback(
        (point: Point, polygonId: string): number | null => {
            const polygon = polygons.find((p) => p.id === polygonId);
            if (!polygon) return null;

            for (let i = 0; i < polygon.points.length; i++) {
                const next = polygon.points[(i + 1) % polygon.points.length];
                const midpoint = {
                    x: (polygon.points[i].x + next.x) / 2,
                    y: (polygon.points[i].y + next.y) / 2,
                };
                if (distance(point, midpoint) <= VERTEX_HIT_RADIUS) return i;
            }
            return null;
        },
        [polygons]
    );

    /** Returns the topmost polygon whose interior contains `point`, or undefined */
    const findPolygonAt = useCallback(
        (point: Point) => [...polygons].reverse().find((polygon) => isPointInsidePolygon(point, polygon)),
        [polygons]
    );

    return { findVertexAt, findEdgeMidpointAt, findPolygonAt };
};
